import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import _ from 'lodash';
import { AvatarModule } from 'primeng/avatar';
import { CheckboxModule } from 'primeng/checkbox';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { TabsModule } from 'primeng/tabs';
import { Textarea } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, of, Subscription } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { QueryOptions } from 'src/app/features/general-agent/general-agent-types';
import { ProjectService } from 'src/app/contract-management/services/project.service';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { SegmentedOption } from 'src/app/@core/components/ui/segmented/segmented.component';
import { IAttachment, IFile, IOrganization, IUser, IWorkspace } from 'src/app/@core/models/model';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { BlurScreenService } from 'src/app/@core/services/blur-screen.service';
import { SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { FileSelectionComponent } from "src/app/@shared/components/file-selection";
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';
import { UserCreditsService } from 'src/app/features/credits/services/user-credits.service';
import { UserCreditsStateService } from 'src/app/features/credits/services/user-credits-state.service';
import { DateFormatterPipe } from 'src/app/@shared/pipes/dateFormater.pipe';
import { SanitizeHtmlPipe } from 'src/app/@shared/pipes/sanitizeHtml.pipe';
import { base64ToBlob } from 'src/app/common/utils';
import { KNOWLEDGE_SCOPE } from 'src/app/contract-management/models/contract-management.model';
import { IDocumentType } from 'src/app/contract-management/models/settings.model';
import { DocumentTypeFilter, DocumentTypeService } from 'src/app/contract-management/services/document-type.service';
import { IKnowledgeBaseStore, KnowledgeBaseService } from 'src/app/contract-management/services/knowledgebase.service';
import { OrganizationSetupFormService } from 'src/app/contract-management/services/organization-setup-from.service';
import { RepositoryService } from 'src/app/contract-management/services/repository.service';
import { TEMPLATE_GENERATION_STATUS, TemplateLibraryService } from 'src/app/contract-management/services/template-library.service';
import { UserService } from 'src/app/contract-management/services/user.service';
import { FilterPipe } from 'src/app/pipes/filter.pipe';
import { FileSizePipe } from 'src/app/shared/pipes/file-size.pipe';
import { HlmDialogHeaderComponent } from '../../../../../@core/components/spartan/dialog/hlm-dialog-header.component';
import { ButtonComponent } from '../../../../../@core/components/ui/button/button.component';
import { DatepickerComponent } from '../../../../../@core/components/ui/datepicker/datepicker.component';
import { DeleteConfirmationComponent } from '../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component';
import { DialogComponent } from '../../../../../@core/components/ui/dialog/dialog.component';
import { IconSourceEnum } from '../../../../../@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from '../../../../../@core/components/ui/menu/menu.component';
import { MultiSelectComponent } from '../../../../../@core/components/ui/multi-select';
import { SelectComponent, SelectConfig, SelectOption } from '../../../../../@core/components/ui/select';
import { SelectButtonComponent } from '../../../../../@core/components/ui/select-button/select-button.component';
import { FileDropComponent } from '../../../../../@shared/components/file-drop/file-drop.component';
import { FileDropTypes } from '../../../../../@shared/components/file-drop/file-drop.types';
import { DropdownOption, FetchFunction, FileItem, FileSelectionChangeEvent, FileSelectionConfig, FileSelectionSubmitEvent, TabType } from '../../../../../@shared/components/file-selection/file-selection.model';
import { GridListViewComponent } from '../../../../../@shared/components/grid-list-view/grid-list-view.component';
import { LottieAnimationComponent } from '../../../../../@shared/components/lottie-animation/lottie-animation.component';
import { SearchBarComponent } from '../../../../../@shared/components/search-bar/search-bar.component';
import { SortFieldOption, SortPopoverComponent } from '../../../../../@shared/components/sort-popover/sort-popover.component';
import { TableColumn, TableConfig } from '../../../../../@shared/models/table.models';
import { KNOWLEDGE_BASE_SVG } from '../../knowledgebase/knowledge-base-svg';
import { TEMPLATE_LIBRARY_TABLE_COLUMNS, TEMPLATE_LIBRARY_TABLE_CONFIG } from './template-library-table.config';
import { ViewTemplateComponent } from './view-template/view-template.component';
import { CreditsOverviewComponent } from "src/app/features/credits/components/credits-overview/credits-overview.component";

const ALL_KNOWLEDGE_FOLDER_PREFIX = 'all-knowledge-';

export enum TEMPLATE_CREATION_METHOD {
    EXTRACT_FROM_DOCUMENT = 'extract-from-document',
    GENERATE_TEMPLATE = 'generate-template',
    MANUAL_CREATE = 'manual-create',
    UPLOAD_TEMPLATE = 'upload-template'
}

export interface IFileOption {
    _id: string;
    name: string;
    size?: string;
    tags?: string[];
}

export interface IKnowledgeBaseOption {
    _id: string;
    name: string;
}

export interface ITemplateLibrary {
    _id: string;
    organization: string | IOrganization;
    workspace: string | IWorkspace;
    name: string;
    templateId: string;
    timesUsed: number;
    description: string;
    attachment: string | IAttachment;
    createdAt: Date;
    updatedAt: Date;
    createdBy?: any;
    createAt?: string;
    url?: string;
    generationStatus?: TEMPLATE_GENERATION_STATUS;
    documentType?: string | IDocumentType;
}

export enum TEMPLATE_LIBRARY_TAB {
    VERSIONS = 'versions',
    ACTIVITY = 'activity'
}

@Component({
    selector: 'app-template-library',
    standalone: true,
    imports: [
    CommonModule,
    FormsModule,
    DialogComponent,
    HlmDialogHeaderComponent,
    InputTextModule,
    TabsModule,
    CheckboxModule,
    TooltipModule,
    Textarea,
    DrawerModule,
    ButtonComponent,
    SearchBarComponent,
    SelectButtonComponent,
    GridListViewComponent,
    FileDropComponent,
    ViewTemplateComponent,
    SanitizeHtmlPipe,
    FileSizePipe,
    SortPopoverComponent,
    DatepickerComponent,
    AvatarModule,
    MultiSelectComponent,
    LottieAnimationComponent,
    DateFormatterPipe,
    DeleteConfirmationComponent,
    IconComponent,
    SelectComponent,
    UserNameDisplayComponent,
    FileSelectionComponent,
    MenuComponent,
    CreditsOverviewComponent
],
    templateUrl: './template-library.component.html',
    styleUrl: './template-library.component.scss'
})
export class TemplateLibraryComponent implements OnInit, AfterViewInit, OnDestroy {
    templates: ITemplateLibrary[] = [];
    viewMode: 'grid' | 'list' = 'grid';
    searchValue: string = '';
    showCreateTemplateDialog: boolean = false;

    // Sort state
    sortField: 'templateId' | 'name' | 'createdAt' | 'updatedAt' = 'createdAt';
    sortOrder: 'asc' | 'desc' = 'desc';
    readonly sortFields: SortFieldOption<'templateId' | 'name' | 'createdAt' | 'updatedAt'>[] = [
        { label: 'Template ID', value: 'templateId' },
        { label: 'Template Name', value: 'name' },
        { label: 'Created Date', value: 'createdAt' },
        { label: 'Last Modified', value: 'updatedAt' },
    ];
    showUploadSection: boolean = false; // Show upload section when Extract from Document is selected
    showFileSelectionView: boolean = false; // Toggle between upload view and file selection view
    showFilePreview: boolean = false; // Show file preview when file is selected from existing files
    showGenerateTemplateSection: boolean = false; // Show generate template section in the same dialog
    showTemplateGenerationDialog: boolean = false; // Show template generation status dialog
    generatingTemplateId: string = ''; // Template whose generation status dialog is currently open
    templateGenerationAnimationData: any = null; // Animation data for template generation
    uploadingAnimation: any = null; // Animation data for uploading files

    // Form fields
    templateName: string = '';
    templateId: string = '';
    description: string = '';
    selectedDocumentTypeId: string | null = null;
    documentTypeOptions: SelectOption[] = [];
    documentTypeConfig: SelectConfig = {
        placeholder: 'Select Document Type',
        searchable: true,
        clearable: false,
        multiple: false,
        appendTo: 'body'
    };
    selectedCreationMethod: TEMPLATE_CREATION_METHOD = TEMPLATE_CREATION_METHOD.EXTRACT_FROM_DOCUMENT;
    generatePrompt: string = ''; // Prompt for generate-template method
    TEMPLATE_CREATION_METHOD = TEMPLATE_CREATION_METHOD; // Expose enum to template

    // Files and knowledge bases from API
    availableFiles: IFile[] = [];
    filteredFiles: IFile[] = [];
    availableKnowledgeBases: IKnowledgeBaseStore[] = [];
    filteredKnowledgeBases: IKnowledgeBaseStore[] = [];
    fileSelectionLoading: boolean = false;
    selectedExistingFile: IFile | null = null;

    // Knowledge base files (from selected KB)
    selectedKnowledgeBaseId: string | null = null;
    knowledgeBaseFiles: IFile[] = [];
    filteredKnowledgeBaseFiles: IFile[] = [];
    knowledgeBaseFilesLoading: boolean = false;
    knowledgeBaseFilesSearchValue: string = '';
    selectedKnowledgeBaseFileIds: Set<string> = new Set();
    selectedKnowledgeBaseFiles: IFile[] = []; // Selected files from knowledge base for preview

    // Track newly completed templates to show tick for 3 seconds
    newlyCompletedTemplates: Set<string> = new Set();
    completionTimeouts: Map<string, any> = new Map();

    // File selection dialog state
    activeTab: string = '0';
    fileSelectionSearchValue: string = '';
    selectedFileIds: Set<string> = new Set();
    selectedKnowledgeBaseIds: Set<string> = new Set();
    selectAllFiles: boolean = false;
    selectAllKnowledgeBases: boolean = false;
    uploadedLocalFiles: File[] = [];

    // File Selection Component state
    openFileSelectionDialog: boolean = false;
    fileSelectionConfig: FileSelectionConfig = {
        maxTotalUploadedAttachments: 1,
        maxTotalUploadedFileSize: 50 * 1000 * 1000,
        localUploadMaxFileSize: 50 * 1000 * 1000,
        title: 'Select file',
        showTotalAttachmentsCount: false,
        supportSubFolders: true,
        allowedFileTypes: [
            FileDropTypes.PDF,
            FileDropTypes.DOCX,
            FileDropTypes.DOC,
            FileDropTypes.ZIP
        ],
        tabs: [
            {
                id: QueryOptions.KNOWLEDGE_BASE,
                label: 'Workspace Knowledge',
                tooltip: 'Select from workspace knowledge',
                type: TabType.SELECT,
                folderIcon: 'book-open-text',
                folderIconSource: 'lucide'
            },
            {
                id: QueryOptions.PROJECT_KNOWLEDGE,
                label: 'Project Knowledge',
                tooltip: 'Select from project knowledge',
                type: TabType.SELECT,
                folderIcon: 'book-open-text',
                folderIconSource: 'lucide'
            },
            {
                id: 'upload',
                label: 'Upload',
                tooltip: 'Upload from local',
                type: TabType.UPLOAD
            }
        ]
    };
    fileSelectionDropdownOptions: DropdownOption[] = [];
    knowledgeBases: IKnowledgeBaseStore[] = [];
    projects: { _id: string; name: string }[] = [];

    // View mode options
    viewModeOptions: SegmentedOption[] = [
        {
            label: '',
            value: 'list',
            icon: 'pi pi-bars',
        },
        {
            label: '',
            value: 'grid',
            icon: 'pi pi-th-large'
        }
    ];

    // Edit Template Dialog
    showEditTemplateDialog: boolean = false;
    selectedTemplateId: string = '';
    selectedTemplate: ITemplateLibrary = {} as ITemplateLibrary;

    // Quick Edit Dialog (Metadata Only)
    showQuickEditDialog: boolean = false;
    quickEditTemplate: ITemplateLibrary | null = null;
    quickEditTemplateName: string = '';
    quickEditTemplateId: string = '';
    quickEditDescription: string = '';
    quickEditDocumentTypeId: string | null = null;
    quickEditLoading: boolean = false;

    // Clone Confirmation Dialog
    showCloneConfirmDialog: boolean = false;
    templateToClone: ITemplateLibrary | null = null;
    cloneLoading: boolean = false;

    // Upload Loading State
    uploadLoading: boolean = false;

    // Delete Confirmation Dialog
    showDeleteConfirmDialog: boolean = false;
    templateToDelete: ITemplateLibrary | null = null;
    deleteLoading: boolean = false;

    // Bulk Selection
    selectedTemplateIds: Set<string> = new Set();
    bulkSelectMode: boolean = false;

    // Bulk Download Confirmation Dialog
    showBulkDownloadConfirmDialog: boolean = false;
    bulkDownloadLoading: boolean = false;

    // Bulk Delete Confirmation Dialog
    showBulkDeleteConfirmDialog: boolean = false;
    bulkDeleteLoading: boolean = false;

    openTemplateMenuId: string | null = null;

    // Workspace Mismatch
    workspaceMismatchDialog: boolean = false;
    templateWorkspaceId: string | null = null;
    currentWorkspace: IWorkspace | null = null;
    currentUser: IUser | null = null;
    workspaces: IWorkspace[] = [];

    // Pagination
    first: number = 0;
    rows: number = 20;
    totalRecords: number = 0;

    // Filter state
    filterPanelVisible: boolean = false;

    // Created Date Filter
    createdDateRange: Date[] | null = null;
    createdDateQuickOption: 'today' | '7days' | '30days' | 'custom' | null = null;

    // Last Modified Date Filter
    lastModifiedDateRange: Date[] | null = null;
    lastModifiedDateQuickOption: 'today' | '7days' | '30days' | 'custom' | null = null;

    // Author Filter
    selectedAuthorIds: string[] = [];
    authorOptions: { label: string; value: string; user: IUser }[] = [];

    // Applied filters (for comparison)
    appliedFilters: {
        createdDateRange: Date[] | null;
        lastModifiedDateRange: Date[] | null;
        selectedAuthorIds: string[];
    } = {
            createdDateRange: null,
            lastModifiedDateRange: null,
            selectedAuthorIds: []
        };

    // Table Configuration
    tableColumns: TableColumn[] = [...TEMPLATE_LIBRARY_TABLE_COLUMNS];
    tableConfig: TableConfig = { ...TEMPLATE_LIBRARY_TABLE_CONFIG };

    // Template References
    @ViewChild('gridItemTemplate') gridItemTemplate!: TemplateRef<any>;
    @ViewChild('templateIdTemplateRef') templateIdTemplateRef!: TemplateRef<any>;
    @ViewChild('templateNameTemplateRef') templateNameTemplateRef!: TemplateRef<any>;
    @ViewChild('templateUsageTemplateRef') templateUsageTemplateRef!: TemplateRef<any>;
    @ViewChild('createdDateTemplateRef') createdDateTemplateRef!: TemplateRef<any>;
    @ViewChild('createdByTemplateRef') createdByTemplateRef!: TemplateRef<any>;
    @ViewChild('viewTemplate') viewTemplateComponent!: ViewTemplateComponent;
    @ViewChild('gridListView') gridListView!: GridListViewComponent;

    filterPipe = new FilterPipe();
    SVGS = KNOWLEDGE_BASE_SVG;

    private templateGenerationStatusSubscription!: Subscription;
    TEMPLATE_GENERATION_STATUS = TEMPLATE_GENERATION_STATUS;

    constructor(
        private templateLibraryService: TemplateLibraryService,
        private toast: ToastService,
        private repositoryService: RepositoryService,
        private knowledgeBaseService: KnowledgeBaseService,
        private cdr: ChangeDetectorRef,
        private userService: UserService,
        private route: ActivatedRoute,
        private router: Router,
        private authService: AuthenticationService,
        private blurScreenService: BlurScreenService,
        private organizationService: OrganizationSetupFormService,
        private socketService: SocketService,
        private documentTypeService: DocumentTypeService,
        private projectService: ProjectService,
        private creditsState: UserCreditsStateService,
        private userCreditsService: UserCreditsService
    ) {
        this.organizationService.currentWorkspace.subscribe(res => this.currentWorkspace = res);
        this.authService.currentUser.subscribe(res => {
            this.currentUser = res as IUser;
        });
    }

    ngOnInit() {
        this.loadTemplates();
        this.loadAuthors();
        this.getWorkspaces();
        this.setupSocketListeners();
        this.creditsState.fetchCredits().subscribe();

        // Check for templateId query parameter (from email notifications)
        this.route.queryParams.subscribe(params => {
            const templateId = params['templateId'];
            if (templateId) {
                // Open the template dialog automatically
                this.openTemplateFromQueryParam(templateId).then(() => {
                    // Only clean up the URL if workspace mismatch dialog is not shown
                    if (!this.workspaceMismatchDialog) {
                        this.router.navigate([], {
                            relativeTo: this.route,
                            queryParams: {},
                            replaceUrl: true
                        });
                    }
                });
            }

            if (params['openAction'] === 'create-template') {
                this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
                setTimeout(() => this.onCreateTemplate(), 300);
            }
        });
    }

    loadTemplates() {
        const filters: {
            skip?: number;
            limit?: number;
            search?: string;
            createdDate?: { from?: string; to?: string };
            lastModifiedDate?: { from?: string; to?: string };
            author?: string;
        } = {
            skip: this.first,
            limit: this.rows,
            search: this.searchValue || undefined
        };

        // Add created date filter
        if (this.appliedFilters.createdDateRange && this.appliedFilters.createdDateRange.length === 2) {
            filters.createdDate = {
                from: this.appliedFilters.createdDateRange[0].toISOString(),
                to: this.appliedFilters.createdDateRange[1].toISOString()
            };
        }

        // Add last modified date filter
        if (this.appliedFilters.lastModifiedDateRange && this.appliedFilters.lastModifiedDateRange.length === 2) {
            filters.lastModifiedDate = {
                from: this.appliedFilters.lastModifiedDateRange[0].toISOString(),
                to: this.appliedFilters.lastModifiedDateRange[1].toISOString()
            };
        }

        // Add author filter (backend currently supports single author, so we use the first selected)
        if (this.appliedFilters.selectedAuthorIds.length > 0) {
            filters.author = this.appliedFilters.selectedAuthorIds[0];
        }

        this.templateLibraryService.getTemplates(filters).subscribe((res) => {
            if (res.success && res.data) {
                this.templates = res.data.data;
                this.totalRecords = res.data.total;
                this.sortTemplates();
                this.cdr.detectChanges();
            } else {
                this.toast.error('Failed to load templates', 'error');
            }
        })
    }

    setSortField(field: 'templateId' | 'name' | 'createdAt' | 'updatedAt') {
        this.sortField = field;
        this.sortTemplates();
    }

    setSortOrder(order: 'asc' | 'desc') {
        this.sortOrder = order;
        this.sortTemplates();
    }

    sortTemplates() {
        this.templates = _.orderBy(
            this.templates,
            [t => {
                if (this.sortField === 'templateId') {
                    return t.templateId?.toLowerCase() || '';
                } else if (this.sortField === 'name') {
                    return t.name?.toLowerCase() || '';
                } else {
                    return new Date(t[this.sortField]).getTime();
                }
            }],
            [this.sortOrder]
        );
    }

    ngAfterViewInit() {
        // Templates map is available via getter
    }

    @ViewChild('actionsTemplateRef') actionsTemplateRef!: TemplateRef<any>;

    get templatesMap(): { [key: string]: TemplateRef<any> } {
        return {
            'templateIdTemplate': this.templateIdTemplateRef,
            'templateNameTemplate': this.templateNameTemplateRef,
            'templateUsageTemplate': this.templateUsageTemplateRef,
            'createdDateTemplate': this.createdDateTemplateRef,
            'createdByTemplate': this.createdByTemplateRef,
            'actionsTemplate': this.actionsTemplateRef
        };
    }

    onSearchChange = _.debounce(() => {
        this.loadTemplates();
    }, 500);

    onCreateTemplate() {
        this.showCreateTemplateDialog = true;
        this.loadUploadingFilesLoaderAnimation();
        this.loadDocumentTypes();
    }

    getTemplateMenuItems(template: ITemplateLibrary): CustomMenuItem[] {
        return [
            {
                label: 'Edit',
                value: 'edit',
                icon: 'pi pi-pencil',
                iconSource: IconSourceEnum.PRIMENG,
            },
            {
                label: 'Clone template',
                value: 'clone',
                icon: 'pi pi-copy',
                iconSource: IconSourceEnum.PRIMENG,
            },
            {
                label: 'Download',
                value: 'download',
                icon: 'pi pi-download',
                iconSource: IconSourceEnum.PRIMENG,
            },
            {
                label: 'Delete',
                value: 'delete',
                icon: 'pi pi-trash',
                iconSource: IconSourceEnum.PRIMENG,
                class: 'text-red-500',
            },
        ];
    }

    handleTemplateMenuAction(item: CustomMenuItem, template: ITemplateLibrary) {
        switch (item.value) {
            case 'edit':
                this.onQuickEdit(template);
                break;
            case 'clone':
                this.onCloneTemplate(template);
                break;
            case 'download':
                this.onDownloadTemplate(template);
                break;
            case 'delete':
                this.onDeleteTemplate(template);
                break;
        }
    }

    onTemplateMenuVisibilityChange(visible: boolean, templateId: string): void {
        if (!visible && this.openTemplateMenuId === templateId) {
            this.openTemplateMenuId = null;
        }
    }

    openTemplateMenu(templateId: string, menuRef: { toggle: (e: Event) => void }, event: Event): void {
        this.openTemplateMenuId = templateId;
        menuRef.toggle(event);
    }


    onSelectCreationMethod(method: TEMPLATE_CREATION_METHOD) {
        this.selectedCreationMethod = method;
    }

    openFileSelectionDialogForTemplate() {
        this.showUploadSection = false;
        this.showFileSelectionView = false;
        this.showFilePreview = false;
        this.loadKnowledgeBasesForFileSelection();
        this.loadProjectsForFileSelection();
        this.openFileSelectionDialog = true;
    }

    loadKnowledgeBasesForFileSelection() {
        this.knowledgeBaseService.getKnowledgeBaseStore({
            skip: 0, limit: 1000, scope: KNOWLEDGE_SCOPE.WORKSPACE
        }).subscribe((res) => {
            if (res && res.success) {
                this.knowledgeBases = res.data.stores || [];
                this.prepareFileSelectionDropdownOptions();
            }
        });
    }

    loadProjectsForFileSelection() {
        this.projectService.getProjects(0, 1000).subscribe((res) => {
            if (res?.success && res?.data?.projects) {
                this.projects = res.data.projects;
            }
        });
    }

    prepareFileSelectionDropdownOptions() {
        const options: DropdownOption[] = [
            { id: 'all', label: 'All files', groupKey: 'metadata.kbId', groupLabel: 'metadata.kbName' }
        ];
        this.knowledgeBases.forEach(kb => {
            options.push({
                id: kb._id,
                label: kb.name,
                value: kb,
                groupKey: 'metadata.kbId',
                groupLabel: 'metadata.kbName'
            });
        });
        this.fileSelectionDropdownOptions = options;
    }

    prepareProjectKnowledgeDropdownOptions() {
        const options: DropdownOption[] = [];
        this.projects.forEach((project) => {
            options.push({
                id: project._id,
                label: project.name,
                value: project,
                groupKey: 'metadata.kbId',
                groupLabel: 'metadata.breadcrumbLabel'
            });
        });
        this.fileSelectionDropdownOptions = options;
    }

    onContinue() {
        if (this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.EXTRACT_FROM_DOCUMENT) {
            // Open file selection dialog instead of showing upload section
            this.openFileSelectionDialogForTemplate();
        } else if (this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.UPLOAD_TEMPLATE) {
            // Open file selection dialog instead of showing upload section
            this.openFileSelectionDialogForTemplate();
        } else if (this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.MANUAL_CREATE) {
            // For manual-create, directly call create without upload dialog
            this.createTemplate();
        } else if (this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.GENERATE_TEMPLATE) {
            // Show generate template section in the same dialog
            this.showGenerateTemplateSection = true;
        }
    }

    goBackToFormFields() {
        this.showUploadSection = false;
        this.showFileSelectionView = false;
        this.showFilePreview = false;
        this.showGenerateTemplateSection = false;
        this.resetSelections();
    }

    onBack() {
        if (this.showFilePreview) {
            // Go back from file preview to file selection view
            // Check if we came from knowledge base files or regular files
            if (this.selectedKnowledgeBaseFiles.length > 0) {
                // Keep knowledge base selection view open and switch to knowledge base tab
                this.showFilePreview = false;
                this.showFileSelectionView = true;
                this.activeTab = '1'; // Switch to knowledge base tab
                // Don't clear selections, just hide preview
            } else {
                // Regular file selection - stay on files tab
                this.selectedExistingFile = null;
                this.selectedFileIds.clear();
                this.showFilePreview = false;
                this.showFileSelectionView = true;
                this.activeTab = '0'; // Stay on files tab
            }
        } else if (this.showFileSelectionView) {
            // Go back from file selection to upload view
            this.goBackToUpload();
        } else if (this.showUploadSection) {
            // Go back from upload section to form fields
            this.goBackToFormFields();
        } else if (this.showGenerateTemplateSection) {
            // Go back from generate template section to form fields
            this.showGenerateTemplateSection = false;
            this.generatePrompt = '';
        } else {
            // Close dialog if in form fields view
            this.onCloseCreateTemplateDialog();
        }
    }


    onExtractFromDocumentUpload(files: File | File[]) {
        // Clear existing file selection when local file is uploaded
        this.selectedExistingFile = null;
        this.selectedFileIds.clear();

        // Only allow single file upload
        if (Array.isArray(files)) {
            this.uploadedLocalFiles = files.slice(0, 1); // Limit to 1 file
        } else {
            this.uploadedLocalFiles = [files];
        }
    }

    onRemoveLocalFile(file: File) {
        this.uploadedLocalFiles = this.uploadedLocalFiles.filter(f => f !== file);
    }

    onUploadFromUploadView() {
        const uploadData = {
            templateName: this.templateName,
            templateId: this.templateId,
            description: this.description,
            creationMethod: this.selectedCreationMethod,
            localFiles: this.uploadedLocalFiles
        };
        console.log('Upload from upload view:', uploadData);
        // Handle the upload data here
        this.onCloseCreateTemplateDialog();
    }


    getFiles() {
        return new Promise((resolve, reject) => {
            this.repositoryService.getFiles().subscribe((res) => {
                if (res && res.success) {
                    this.availableFiles = res.data || [];
                    this.filteredFiles = [...this.availableFiles];
                    resolve(true);
                } else {
                    reject(false);
                }
            });
        });
    }

    getKnowledgeBaseStore() {
        return new Promise((resolve, reject) => {
            this.knowledgeBaseService.getKnowledgeBaseStore({ skip: 0, limit: 1000, scope: KNOWLEDGE_SCOPE.WORKSPACE }).subscribe((res) => {
                if (res && res.success) {
                    this.availableKnowledgeBases = res.data.stores || [];
                    this.filteredKnowledgeBases = [...this.availableKnowledgeBases];
                    resolve(true);
                } else {
                    reject(false);
                }
            });
        });
    }

    onFileSelectionSearch() {
        this.filteredFiles = this.fileSelectionSearchValue
            ? this.getFiltered(this.fileSelectionSearchValue, this.availableFiles, 'name')
            : [...this.availableFiles];
    }

    onKnowledgeBaseSearch() {
        this.filteredKnowledgeBases = this.fileSelectionSearchValue
            ? this.getFiltered(this.fileSelectionSearchValue, this.availableKnowledgeBases, 'name')
            : [...this.availableKnowledgeBases];
    }

    onKnowledgeBaseClick(kbId: string) {
        if (this.selectedKnowledgeBaseId === kbId) {
            // Deselect if already selected
            this.selectedKnowledgeBaseId = null;
            this.knowledgeBaseFiles = [];
            this.filteredKnowledgeBaseFiles = [];
            this.selectedKnowledgeBaseFileIds.clear();
        } else {
            // Select new knowledge base
            this.selectedKnowledgeBaseId = kbId;
            this.loadKnowledgeBaseFiles(kbId);
        }
    }

    loadKnowledgeBaseFiles(kbId: string) {
        this.knowledgeBaseFilesLoading = true;
        this.knowledgeBaseService.getKnowledgeBaseStoreById(kbId).subscribe((res) => {
            if (res && res.success && res.data) {
                // Extract files from knowledge base store
                this.knowledgeBaseFiles = res.data.files?.map((fileItem: any) => fileItem.file).filter((file: IFile) => file) || [];
                this.filteredKnowledgeBaseFiles = [...this.knowledgeBaseFiles];
                this.knowledgeBaseFilesLoading = false;
                this.cdr.detectChanges();
            } else {
                this.knowledgeBaseFilesLoading = false;
                this.toast.error('Failed to load knowledge base files', 'error');
            }
        });
    }

    onKnowledgeBaseFilesSearch() {
        this.filteredKnowledgeBaseFiles = this.knowledgeBaseFilesSearchValue
            ? this.getFiltered(this.knowledgeBaseFilesSearchValue, this.knowledgeBaseFiles, 'name')
            : [...this.knowledgeBaseFiles];
    }

    isKnowledgeBaseFileSelected(fileId: string): boolean {
        return this.selectedKnowledgeBaseFileIds.has(fileId);
    }

    setKnowledgeBaseFileSelectedState(fileId: string, selected: boolean) {
        if (selected) {
            // Clear regular file selection when selecting knowledge base files
            this.selectedExistingFile = null;
            this.selectedFileIds.clear();

            this.selectedKnowledgeBaseFileIds.add(fileId);
            // Add file to selected files array for preview
            const file = this.knowledgeBaseFiles.find(f => f._id === fileId);
            if (file && !this.selectedKnowledgeBaseFiles.find(f => f._id === fileId)) {
                this.selectedKnowledgeBaseFiles.push(file);
            }
        } else {
            this.selectedKnowledgeBaseFileIds.delete(fileId);
            // Remove file from selected files array
            this.selectedKnowledgeBaseFiles = this.selectedKnowledgeBaseFiles.filter(f => f._id !== fileId);
        }

        // Show preview if any files are selected, hide file selection view
        if (this.selectedKnowledgeBaseFileIds.size > 0) {
            this.showFilePreview = true;
            this.showFileSelectionView = false;
        } else {
            this.showFilePreview = false;
            this.showFileSelectionView = true;
        }
    }

    private getFiltered<T>(searchValue: string, sourceArray: T[], filterProperty: string): T[] {
        return this.filterPipe.transform(sourceArray, filterProperty, searchValue);
    }

    // Helper method to download file from base64
    private downloadFileFromBase64(buffer: string, filename: string, mimeType: string): void {
        const blob = base64ToBlob(buffer, mimeType);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }

    // Helper method for date quick options
    private setDateRange(option: 'today' | '7days' | '30days' | 'custom', currentOption: 'today' | '7days' | '30days' | 'custom' | null, currentRange: Date[] | null): { option: 'today' | '7days' | '30days' | 'custom' | null, range: Date[] | null } {
        // Toggle: if already selected, deselect it
        if (currentOption === option) {
            return { option: null, range: null };
        }

        const today = new Date();
        today.setHours(23, 59, 59, 999);
        let range: Date[] | null = null;

        switch (option) {
            case 'today':
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                range = [todayStart, today];
                break;
            case '7days':
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                sevenDaysAgo.setHours(0, 0, 0, 0);
                range = [sevenDaysAgo, today];
                break;
            case '30days':
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                thirtyDaysAgo.setHours(0, 0, 0, 0);
                range = [thirtyDaysAgo, today];
                break;
            case 'custom':
                range = currentRange;
                break;
        }

        return { option, range };
    }

    openFileSelectionView() {
        this.showFileSelectionView = true;
        this.activeTab = '0';
        this.resetFileSelectionState();
        this.fileSelectionLoading = true;

        // Fetch files and knowledge bases if not already loaded
        Promise.all([this.getFiles(), this.getKnowledgeBaseStore()]).then(() => {
            this.fileSelectionLoading = false;
            this.cdr.detectChanges();
        }).catch(() => {
            this.fileSelectionLoading = false;
            this.toast.error('Failed to load files and knowledge bases', 'error');
        });
    }

    goBackToUpload() {
        this.showFileSelectionView = false;
        this.showFilePreview = false;
        this.resetFileSelectionState();
    }

    onDeleteSelectedFile() {
        this.selectedExistingFile = null;
        this.selectedFileIds.clear();
        this.showFilePreview = false;
        this.showFileSelectionView = true;
    }

    onDeleteKnowledgeBaseFile(fileId: string) {
        this.selectedKnowledgeBaseFileIds.delete(fileId);
        this.selectedKnowledgeBaseFiles = this.selectedKnowledgeBaseFiles.filter(f => f._id !== fileId);

        // If no files selected, go back to file selection view
        if (this.selectedKnowledgeBaseFileIds.size === 0 && !this.selectedExistingFile) {
            this.showFilePreview = false;
            this.showFileSelectionView = true;
        }
    }

    onCloseCreateTemplateDialog() {
        this.showCreateTemplateDialog = false;
        this.showUploadSection = false;
        this.showFileSelectionView = false;
        this.showFilePreview = false;
        this.showGenerateTemplateSection = false;
        this.uploadLoading = false;
        this.resetForm();
        this.resetSelections();
    }

    resetFileSelectionState(clearLocalFiles: boolean = false) {
        // Reset file selection state
        this.selectedFileIds.clear();
        this.selectedKnowledgeBaseIds.clear();
        this.selectAllFiles = false;
        this.selectAllKnowledgeBases = false;
        this.fileSelectionSearchValue = '';
        this.selectedExistingFile = null;
        this.showFilePreview = false;
        // Reset knowledge base files selection
        this.selectedKnowledgeBaseId = null;
        this.knowledgeBaseFiles = [];
        this.filteredKnowledgeBaseFiles = [];
        this.selectedKnowledgeBaseFileIds.clear();
        this.selectedKnowledgeBaseFiles = [];
        this.knowledgeBaseFilesSearchValue = '';
        if (clearLocalFiles) {
            this.uploadedLocalFiles = [];
        }
    }

    resetSelections() {
        this.resetFileSelectionState(true);
    }

    toggleFileSelection(file: IFile) {
        if (this.selectedExistingFile?._id === file._id) {
            // Deselect if already selected
            this.selectedExistingFile = null;
            this.selectedFileIds.clear();
            this.showFilePreview = false;
        } else {
            // Single select - only one file can be selected
            this.selectedExistingFile = file;
            this.selectedFileIds.clear();
            this.selectedFileIds.add(file._id);
            // Show preview when file is selected
            this.showFilePreview = true;
            this.showFileSelectionView = false;
        }
        this.selectAllFiles = false;
    }

    toggleKnowledgeBaseSelection(kbId: string) {
        if (this.selectedKnowledgeBaseIds.has(kbId)) {
            this.selectedKnowledgeBaseIds.delete(kbId);
        } else {
            this.selectedKnowledgeBaseIds.clear(); // Single select
            this.selectedKnowledgeBaseIds.add(kbId);
        }
        this.selectAllKnowledgeBases = false;
        this.updateSelectAllKnowledgeBases();
    }


    toggleSelectAllKnowledgeBases() {
        this.selectAllKnowledgeBases = !this.selectAllKnowledgeBases;
        if (this.selectAllKnowledgeBases) {
            this.filteredKnowledgeBases.forEach(kb => this.selectedKnowledgeBaseIds.add(kb._id));
        } else {
            this.filteredKnowledgeBases.forEach(kb => this.selectedKnowledgeBaseIds.delete(kb._id));
        }
    }

    updateSelectAllKnowledgeBases() {
        this.selectAllKnowledgeBases = this.filteredKnowledgeBases.length > 0 &&
            this.filteredKnowledgeBases.every(kb => this.selectedKnowledgeBaseIds.has(kb.name));
    }

    isFileSelected(file: IFile): boolean {
        return this.selectedExistingFile?._id === file._id;
    }

    isKnowledgeBaseSelected(kbId: string): boolean {
        return this.selectedKnowledgeBaseIds.has(kbId);
    }

    setFileSelectedState(file: IFile, selected: boolean) {
        if (selected) {
            // Clear knowledge base file selections when selecting regular file
            this.selectedKnowledgeBaseFileIds.clear();
            this.selectedKnowledgeBaseFiles = [];

            this.toggleFileSelection(file);
            // Show preview when file is selected
            this.showFilePreview = true;
            this.showFileSelectionView = false;
        } else {
            if (this.selectedExistingFile?._id === file._id) {
                this.selectedExistingFile = null;
                this.selectedFileIds.clear();
                this.showFilePreview = false;
            }
        }
    }

    setKnowledgeBaseSelectedState(kbId: string, selected: boolean) {
        if (selected) {
            this.toggleKnowledgeBaseSelection(kbId);
        } else {
            this.selectedKnowledgeBaseIds.delete(kbId);
            this.updateSelectAllKnowledgeBases();
        }
    }

    createTemplate() {
        // Validate required fields
        if (!this.templateName || !this.templateId || !this.selectedDocumentTypeId) {
            this.toast.error('Please fill in all required fields', 'error');
            return;
        }

        this.uploadLoading = true;

        const uploadData = {
            templateName: this.templateName,
            templateId: this.templateId,
            description: this.description,
            creationMethod: this.selectedCreationMethod,
        };

        const formData = new FormData();
        formData.append('name', uploadData.templateName);
        formData.append('templateId', uploadData.templateId);
        formData.append('description', uploadData.description);
        formData.append('creationMethod', uploadData.creationMethod);

        // Add document type ID (required field)
        formData.append('documentType', this.selectedDocumentTypeId);

        // Add prompt if generate-template method is selected
        if (this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.GENERATE_TEMPLATE && this.generatePrompt) {
            formData.append('prompt', this.generatePrompt);
        }

        // Only send files/selectedfileid if NOT manual-create and NOT generate-template
        if (this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.EXTRACT_FROM_DOCUMENT || this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.UPLOAD_TEMPLATE) {
            // Priority: local file upload > existing file selection > knowledge base files
            if (this.uploadedLocalFiles.length > 0) {
                // If local file is uploaded, send it in 'files' field
                this.uploadedLocalFiles.forEach((file: File) => {
                    formData.append('files', file);
                });
            } else if (this.selectedExistingFile) {
                // If existing file is selected, send only the _id as 'selectedfileid'
                formData.append('selectedfileid', this.selectedExistingFile._id);
            } else if (this.selectedKnowledgeBaseFileIds.size > 0) {
                // If files from knowledge base are selected, send their IDs
                // Send as array - append each file ID
                Array.from(this.selectedKnowledgeBaseFileIds).forEach((fileId: string) => {
                    formData.append('selectedfileid', fileId);
                });
            }
        }

        this.templateLibraryService.createTemplate(formData).subscribe({
            next: (res) => {
                this.uploadLoading = false;
                if (res.success && res.data) {
                    // Show template generation dialog for EXTRACT_FROM_DOCUMENT and GENERATE_TEMPLATE methods
                    if (this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.EXTRACT_FROM_DOCUMENT ||
                        this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.GENERATE_TEMPLATE) {
                        this.showTemplateGenerationDialog = true;
                        this.loadTemplateGenerationAnimation();
                        this.loadTemplates();
                        // Close the create template dialog
                        this.onCloseCreateTemplateDialog();
                    } else if (this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.MANUAL_CREATE ||
                        this.selectedCreationMethod === TEMPLATE_CREATION_METHOD.UPLOAD_TEMPLATE) {
                        // For manual create and upload template, open view-template directly
                        this.toast.success('Template created successfully', 'success');
                        this.loadTemplates();
                        this.onCloseCreateTemplateDialog();
                        // Open the created template in view-template dialog
                        this.selectedTemplateId = res.data._id;
                        this.selectedTemplate = res.data;
                        this.showEditTemplateDialog = true;
                    } else {
                        this.toast.success('Template created successfully', 'success');
                        this.loadTemplates();
                        this.onCloseCreateTemplateDialog();
                    }
                }
            },
            error: (error) => {
                this.uploadLoading = false;
                this.toast.error(error?.message || 'Failed to create template', 'error');
            }
        });
    }

    onGenerateTemplate() {
        // Call create template from the generate template section
        this.createTemplate();
    }

    loadTemplateGenerationAnimation() {
        // Load the template generation animation data from file
        fetch('/animations/template_generation.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                this.templateGenerationAnimationData = data;
            })
            .catch(error => {
                console.error('Error loading template generation animation:', error);
            });
    }

    onCloseTemplateGenerationDialog() {
        this.showTemplateGenerationDialog = false;
        this.templateGenerationAnimationData = null;
        this.generatingTemplateId = '';
    }

    onUploadSelected() {
        this.createTemplate();
    }

    get canUpload(): boolean {
        if (this.showFilePreview) {
            return this.selectedExistingFile !== null || this.selectedKnowledgeBaseFiles.length > 0;
        }
        return this.selectedExistingFile !== null ||
            this.selectedKnowledgeBaseFileIds.size > 0 ||
            this.uploadedLocalFiles.length > 0;
    }

    onCancel() {
        this.onCloseCreateTemplateDialog();
    }

    private resetForm() {
        this.templateName = '';
        this.templateId = '';
        this.description = '';
        this.selectedDocumentTypeId = null;
        this.selectedCreationMethod = TEMPLATE_CREATION_METHOD.EXTRACT_FROM_DOCUMENT;
        this.uploadedLocalFiles = [];
        this.selectedExistingFile = null;
        this.generatePrompt = '';
    }

    loadDocumentTypes() {
        this.documentTypeService.getDocTypes([DocumentTypeFilter.GENERAL, DocumentTypeFilter.FORM_RELATED]).subscribe((res) => {
            if (res && res.success && res.data) {
                this.documentTypeOptions = res.data.map((docType: IDocumentType) => ({
                    label: docType.name,
                    value: docType._id
                }));
            }
        });
    }

    onViewModeChange(mode: 'grid' | 'list') {
        this.viewMode = mode;
    }


    onFilterClick() {
        this.filterPanelVisible = true;
    }

    // Load authors for filter
    loadAuthors() {
        this.userService.getWorkspaceUsers({ role: [], includeAdmins: true }).subscribe((res) => {
            if (res.success && res.data) {
                this.authorOptions = res.data.map((user: IUser) => ({
                    label: user.fullName || user.email || '',
                    value: user._id,
                    user: user
                }));
            }
        });
    }

    // Created Date Quick Options
    selectCreatedDateQuickOption(option: 'today' | '7days' | '30days' | 'custom') {
        const result = this.setDateRange(option, this.createdDateQuickOption, this.createdDateRange);
        this.createdDateQuickOption = result.option;
        this.createdDateRange = result.range;
    }

    // Last Modified Date Quick Options
    selectLastModifiedDateQuickOption(option: 'today' | '7days' | '30days' | 'custom') {
        const result = this.setDateRange(option, this.lastModifiedDateQuickOption, this.lastModifiedDateRange);
        this.lastModifiedDateQuickOption = result.option;
        this.lastModifiedDateRange = result.range;
    }

    // Handle custom date range selection
    onCreatedDateRangeChange() {
        this.createdDateQuickOption = (this.createdDateRange && this.createdDateRange.length === 2) ? 'custom' : null;
    }

    onLastModifiedDateRangeChange() {
        this.lastModifiedDateQuickOption = (this.lastModifiedDateRange && this.lastModifiedDateRange.length === 2) ? 'custom' : null;
    }

    // Clear individual filters
    clearCreatedDateFilter() {
        this.createdDateRange = null;
        this.createdDateQuickOption = null;
    }

    clearLastModifiedDateFilter() {
        this.lastModifiedDateRange = null;
        this.lastModifiedDateQuickOption = null;
    }

    clearAuthorFilter() {
        this.selectedAuthorIds = [];
    }

    // Check if any filters are set
    get hasActiveFilters(): boolean {
        return !!(
            (this.createdDateRange && this.createdDateRange.length === 2) ||
            (this.lastModifiedDateRange && this.lastModifiedDateRange.length === 2) ||
            this.selectedAuthorIds.length > 0
        );
    }

    // Apply filters
    applyFilters() {
        // Update applied filters
        this.appliedFilters = {
            createdDateRange: this.createdDateRange && this.createdDateRange.length === 2
                ? [...this.createdDateRange]
                : null,
            lastModifiedDateRange: this.lastModifiedDateRange && this.lastModifiedDateRange.length === 2
                ? [...this.lastModifiedDateRange]
                : null,
            selectedAuthorIds: [...this.selectedAuthorIds]
        };

        // Reset pagination
        this.first = 0;

        // Reload templates with filters
        this.loadTemplates();

        // Close filter panel
        this.filterPanelVisible = false;
    }

    // Clear all filters
    clearAllFilters() {
        this.createdDateRange = null;
        this.createdDateQuickOption = null;
        this.lastModifiedDateRange = null;
        this.lastModifiedDateQuickOption = null;
        this.selectedAuthorIds = [];

        // Reset applied filters
        this.appliedFilters = {
            createdDateRange: null,
            lastModifiedDateRange: null,
            selectedAuthorIds: []
        };

        // Reset pagination
        this.first = 0;

        // Reload templates without filters
        this.loadTemplates();
    }

    onPageChange(event: any) {
        this.first = event.first ?? 0;
        this.rows = event.rows ?? 20;
        this.loadTemplates();
    }


    onQuickEdit(template: ITemplateLibrary) {
        this.quickEditTemplate = template;
        this.quickEditTemplateName = template.name;
        this.quickEditTemplateId = template.templateId;
        this.quickEditDescription = template.description || '';
        // Set document type ID - handle both string and object types
        if (template.documentType) {
            this.quickEditDocumentTypeId = typeof template.documentType === 'string'
                ? template.documentType
                : template.documentType._id;
        } else {
            this.quickEditDocumentTypeId = null;
        }
        this.loadDocumentTypes();
        this.showQuickEditDialog = true;
    }

    onCloseQuickEditDialog() {
        this.showQuickEditDialog = false;
        this.quickEditTemplate = null;
        this.quickEditTemplateName = '';
        this.quickEditTemplateId = '';
        this.quickEditDescription = '';
        this.quickEditDocumentTypeId = null;
    }

    onSaveQuickEdit() {
        if (!this.quickEditTemplate || !this.quickEditTemplateName || !this.quickEditTemplateId || !this.quickEditDocumentTypeId) {
            this.toast.error('Please fill in all required fields', 'error');
            return;
        }

        const updateData: { name?: string; description?: string; templateId?: string; documentType?: string } = {};

        if (this.quickEditTemplateName !== this.quickEditTemplate.name) {
            updateData.name = this.quickEditTemplateName;
        }
        if (this.quickEditTemplateId !== this.quickEditTemplate.templateId) {
            updateData.templateId = this.quickEditTemplateId;
        }
        if (this.quickEditDescription !== (this.quickEditTemplate.description || '')) {
            updateData.description = this.quickEditDescription;
        }

        // Check if document type has changed
        const currentDocumentTypeId = this.quickEditTemplate.documentType
            ? (typeof this.quickEditTemplate.documentType === 'string'
                ? this.quickEditTemplate.documentType
                : this.quickEditTemplate.documentType._id)
            : null;

        if (this.quickEditDocumentTypeId !== currentDocumentTypeId) {
            updateData.documentType = this.quickEditDocumentTypeId;
        }

        // Only call API if there are changes
        if (Object.keys(updateData).length === 0) {
            this.onCloseQuickEditDialog();
            return;
        }

        this.quickEditLoading = true;
        this.templateLibraryService.updateTemplateMetadata(this.quickEditTemplate._id, updateData).subscribe({
            next: (res) => {
                this.quickEditLoading = false;
                if (res.success && res.data) {
                    // Update in templates array
                    const index = this.templates.findIndex(t => t._id === this.quickEditTemplate!._id);
                    if (index !== -1) {
                        this.templates[index] = res.data;
                    }
                    this.toast.success('Template metadata updated successfully', 'success');
                    this.onCloseQuickEditDialog();
                    // Reload templates to ensure consistency
                    this.loadTemplates();
                } else {
                    this.toast.error('Failed to update template metadata', 'error');
                }
            },
            error: (error) => {
                this.quickEditLoading = false;
                this.toast.error(error?.message || 'Failed to update template metadata', 'error');
            }
        });
    }

    onCloneTemplate(template: ITemplateLibrary) {
        this.templateToClone = template;
        this.showCloneConfirmDialog = true;
    }

    onCloseCloneConfirmDialog() {
        this.showCloneConfirmDialog = false;
        this.templateToClone = null;
    }

    onConfirmClone() {
        if (!this.templateToClone) {
            return;
        }

        this.cloneLoading = true;
        this.templateLibraryService.cloneTemplate(this.templateToClone._id).subscribe({
            next: (res) => {
                this.cloneLoading = false;
                if (res.success && res.data) {
                    this.toast.success('Template cloned successfully', 'success');
                    this.onCloseCloneConfirmDialog();
                    // Reload templates to get the updated list with the cloned template
                    this.loadTemplates();
                } else {
                    this.toast.error('Failed to clone template', 'error');
                }
            },
            error: (error) => {
                this.cloneLoading = false;
                this.toast.error(error?.message || 'Failed to clone template', 'error');
            }
        });
    }

    onDownloadTemplate(template: ITemplateLibrary) {
        // Use bulk download API even for single template
        this.templateLibraryService.bulkDownloadTemplates([template._id]).subscribe({
            next: (res: any) => {
                if (res.success && res.data && res.data.length > 0) {
                    const templateData = res.data[0];
                    this.downloadFileFromBase64(templateData.buffer, templateData.filename, templateData.mimeType);
                    this.toast.success('Template downloaded successfully', 'success');
                } else {
                    this.toast.error('Failed to download template', 'error');
                }
            },
            error: (error: any) => {
                this.toast.error(error?.message || 'Failed to download template', 'error');
            }
        });
    }

    onDeleteTemplate(template: ITemplateLibrary) {
        this.templateToDelete = template;
        this.showDeleteConfirmDialog = true;
    }

    onCloseDeleteConfirmDialog() {
        this.showDeleteConfirmDialog = false;
        this.templateToDelete = null;
    }

    onConfirmDelete() {
        if (!this.templateToDelete) {
            return;
        }

        this.deleteLoading = true;
        // Use bulk delete API even for single template
        this.templateLibraryService.bulkDeleteTemplates([this.templateToDelete._id]).subscribe({
            next: (res: any) => {
                this.deleteLoading = false;
                if (res.success) {
                    // Remove from templates array
                    this.templates = this.templates.filter(t => t._id !== this.templateToDelete!._id);
                    this.totalRecords = this.totalRecords - 1;
                    this.toast.success('Template deleted successfully', 'success');
                    this.onCloseDeleteConfirmDialog();
                } else {
                    this.toast.error('Failed to delete template', 'error');
                }
            },
            error: (error: any) => {
                this.deleteLoading = false;
                this.toast.error(error?.message || 'Failed to delete template', 'error');
            }
        });
    }

    onTemplateClick(template: ITemplateLibrary) {
        this.openTemplateEditor(template);
    }

    openTemplateEditor(template: ITemplateLibrary) {
        // Check template generation status before opening dialog
        this.templateLibraryService.getTemplate(template._id).subscribe({
            next: (res: any) => {
                if (res.success && res.data) {
                    // If template is still being generated (or paused for credit exhaustion), show generation dialog
                    if (res.data.generationStatus === TEMPLATE_GENERATION_STATUS.IN_PROGRESS || res.data.generationStatus === TEMPLATE_GENERATION_STATUS.CREDITS_EXHAUSTED) {
                        this.generatingTemplateId = template._id;
                        this.showTemplateGenerationDialog = true;
                        this.loadTemplateGenerationAnimation();
                        return;
                    }
                    // Check workspace mismatch before opening dialog
                    this.selectedTemplate = res.data;
                    this.checkIfSameWorkspace();
                    if (!this.workspaceMismatchDialog) {
                        // Otherwise, open the dialog normally
                        this.selectedTemplateId = template._id;
                        this.showEditTemplateDialog = true;
                    }
                } else {
                    this.toast.error('Failed to load template', 'error');
                }
            },
            error: () => {
                this.toast.error('Failed to load template', 'error');
            }
        });
    }

    openTemplateFromQueryParam(templateId: string): Promise<void> {
        return new Promise((resolve) => {
            // Fetch template by ID and open dialog
            this.templateLibraryService.getTemplate(templateId).subscribe({
                next: (res: any) => {
                    if (res.success && res.data) {
                        if (res.data.generationStatus === TEMPLATE_GENERATION_STATUS.IN_PROGRESS
                            || res.data.generationStatus === TEMPLATE_GENERATION_STATUS.CREDITS_EXHAUSTED) {
                            this.generatingTemplateId = templateId;
                            this.showTemplateGenerationDialog = true;
                            this.loadTemplateGenerationAnimation();
                            resolve();
                            return;
                        }
                        // Check workspace mismatch before opening dialog
                        this.selectedTemplate = res.data;
                        this.checkIfSameWorkspace();
                        if (!this.workspaceMismatchDialog) {
                            // Open the dialog with the template
                            this.selectedTemplateId = templateId;
                            this.showEditTemplateDialog = true;
                        }
                        resolve();
                    } else {
                        this.toast.error('Template not found', 'error');
                        resolve();
                    }
                },
                error: () => {
                    this.toast.error('Failed to load template', 'error');
                    resolve();
                }
            });
        });
    }

    onCloseEditTemplateDialog() {
        this.showEditTemplateDialog = false;
        this.selectedTemplateId = '';
    }

    onDownloadCurrentTemplate() {
        if (this.viewTemplateComponent) {
            this.viewTemplateComponent.onDownloadTemplate();
        }
    }

    // Bulk Selection Methods
    onTableSelectionChange(selected: any[]) {
        this.selectedTemplateIds = new Set((selected || []).map((t: any) => t?._id).filter(Boolean));
    }

    toggleTemplateSelection(templateId: string) {
        if (this.selectedTemplateIds.has(templateId)) {
            this.selectedTemplateIds.delete(templateId);
        } else {
            this.selectedTemplateIds.add(templateId);
        }
    }

    isTemplateSelected(templateId: string): boolean {
        return this.selectedTemplateIds.has(templateId);
    }

    loadUploadingFilesLoaderAnimation() {
        fetch('/animations/uploadingFilesLoader.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                this.uploadingAnimation = data;
            })
            .catch(error => {
                console.error('Error loading welcome animation:', error);
            });
    }

    get selectedTemplatesCount(): number {
        return this.selectedTemplateIds.size;
    }

    selectAllTemplates() {
        this.templates.forEach(template => {
            this.selectedTemplateIds.add(template._id);
        });
    }

    deselectAllTemplates() {
        this.selectedTemplateIds.clear();
    }

    // Bulk Download Methods
    onBulkDownload() {
        if (this.selectedTemplateIds.size === 0) {
            return;
        }
        this.showBulkDownloadConfirmDialog = true;
    }

    onCloseBulkDownloadConfirmDialog() {
        this.showBulkDownloadConfirmDialog = false;
    }

    onConfirmBulkDownload() {
        if (this.selectedTemplateIds.size === 0) {
            return;
        }

        this.bulkDownloadLoading = true;
        const templateIds = Array.from(this.selectedTemplateIds);

        this.templateLibraryService.bulkDownloadTemplates(templateIds).subscribe({
            next: (res: any) => {
                this.bulkDownloadLoading = false;
                if (res.success && res.data) {
                    res.data.forEach((templateData: { templateId: string; filename: string; buffer: string; mimeType: string }) => {
                        this.downloadFileFromBase64(templateData.buffer, templateData.filename, templateData.mimeType);
                    });

                    this.toast.success(`${templateIds.length} template(s) downloaded successfully`, 'success');
                    this.onCloseBulkDownloadConfirmDialog();
                    this.selectedTemplateIds.clear();
                    this.gridListView?.clearSelection?.();
                } else {
                    this.toast.error('Failed to download templates', 'error');
                }
            },
            error: (error: any) => {
                this.bulkDownloadLoading = false;
                this.toast.error(error?.message || 'Failed to download templates', 'error');
            }
        });
    }

    // Bulk Delete Methods
    onBulkDelete() {
        if (this.selectedTemplateIds.size === 0) {
            return;
        }
        this.showBulkDeleteConfirmDialog = true;
    }

    onCloseBulkDeleteConfirmDialog() {
        this.showBulkDeleteConfirmDialog = false;
    }

    onConfirmBulkDelete() {
        if (this.selectedTemplateIds.size === 0) {
            return;
        }

        this.bulkDeleteLoading = true;
        const templateIds = Array.from(this.selectedTemplateIds);

        this.templateLibraryService.bulkDeleteTemplates(templateIds).subscribe({
            next: (res: any) => {
                this.bulkDeleteLoading = false;
                if (res.success) {
                    // Remove deleted templates from the array
                    this.templates = this.templates.filter(t => !this.selectedTemplateIds.has(t._id));
                    this.totalRecords = this.totalRecords - templateIds.length;
                    this.toast.success(`${templateIds.length} template(s) deleted successfully`, 'success');
                    this.onCloseBulkDeleteConfirmDialog();
                    this.selectedTemplateIds.clear();
                    this.gridListView?.clearSelection?.();
                } else {
                    this.toast.error('Failed to delete templates', 'error');
                }
            },
            error: (error: any) => {
                this.bulkDeleteLoading = false;
                this.toast.error(error?.message || 'Failed to delete templates', 'error');
            }
        });
    }

    getWorkspaces() {
        this.organizationService.getWorkspaces().subscribe({
            next: (res) => {
                if (res.success) {
                    this.workspaces = res.data;
                } else {
                    this.workspaces = [];
                }
            },
            error: (error) => {
                console.error('Error fetching workspaces:', error);
                this.workspaces = [];
            }
        });
    }

    checkIfSameWorkspace() {
        if (!this.selectedTemplate) return;

        const templateWorkspaceId = this.selectedTemplate.workspace as string
        const currentWorkspaceId = this.currentWorkspace?._id;

        // If workspaces haven't loaded yet, wait a bit and check again
        if (this.workspaces.length === 0 && currentWorkspaceId) {
            setTimeout(() => {
                this.checkIfSameWorkspace();
            }, 500);
            return;
        }

        const hasAccessToTemplateWorkspace = this.workspaces.some(workspace => workspace._id === templateWorkspaceId);

        if (
            (templateWorkspaceId && templateWorkspaceId !== currentWorkspaceId) &&
            hasAccessToTemplateWorkspace &&
            !this.currentUser?.anonymous
        ) {
            this.templateWorkspaceId = templateWorkspaceId;
            this.workspaceMismatchDialog = true;
        }
    }

    switchToTemplateWorkspace() {
        if (!this.templateWorkspaceId || !this.currentUser?._id) {
            this.toast.error('Unable to switch workspace');
            return;
        }

        this.userService.updateUser({
            _id: this.currentUser._id,
            currentWorkspace: this.templateWorkspaceId
        }).subscribe((res) => {
            if (res.success) {
                this.authService.setUserData(res.data);
                this.blurScreenService.enableBlur();
                this.workspaceMismatchDialog = false;
                // Keep query params so template opens after reload
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                this.toast.error('Failed to switch workspace');
            }
        });
    }

    setupSocketListeners() {
        this.templateGenerationStatusSubscription = this.socketService
            .on<{ templateId: string, status: TEMPLATE_GENERATION_STATUS, template: ITemplateLibrary }>(SOCKET_EVENTS.TEMPLATE_GENERATION_STATUS_CHANGED)
            .subscribe(res => {
                if (res && res[0]) {
                    const { templateId, status } = res[0];
                    this.updateTemplateStatus(templateId, status);
                }
            });
    }

    updateTemplateStatus(templateId: string, status: TEMPLATE_GENERATION_STATUS) {
        // Find the current template to check its previous status
        const currentTemplate = this.templates.find(t => t._id === templateId);
        const wasInProgress = currentTemplate?.generationStatus === TEMPLATE_GENERATION_STATUS.IN_PROGRESS;

        this.templates = this.templates.map(template =>
            template._id === templateId
                ? { ...template, generationStatus: status }
                : template
        ) as ITemplateLibrary[];

        // If template just completed (was IN_PROGRESS, now COMPLETED), show tick for 3 seconds
        if (wasInProgress && status === TEMPLATE_GENERATION_STATUS.COMPLETED) {
            this.newlyCompletedTemplates.add(templateId);

            // Clear any existing timeout for this template
            if (this.completionTimeouts.has(templateId)) {
                clearTimeout(this.completionTimeouts.get(templateId));
            }

            // Set new timeout to remove from newly completed after 3 seconds
            const timeout = setTimeout(() => {
                this.newlyCompletedTemplates.delete(templateId);
                this.completionTimeouts.delete(templateId);
                this.cdr.detectChanges();
            }, 5000);

            this.completionTimeouts.set(templateId, timeout);
        }

        this.cdr.detectChanges();
    }

    // Check if template should show completion tick instead of menu
    shouldShowCompletionTick(templateId: string): boolean {
        return this.newlyCompletedTemplates.has(templateId);
    }

    isTemplateGenerating(template: ITemplateLibrary): boolean {
        return template.generationStatus === TEMPLATE_GENERATION_STATUS.IN_PROGRESS
            || template.generationStatus === TEMPLATE_GENERATION_STATUS.CREDITS_EXHAUSTED;
    }

    // Check if a template's generation is paused because agent credits are exhausted
    isTemplateCreditsExhausted(templateId: string): boolean {
        return (this.creditsState.credits()?.exhaustions ?? []).some(e => e.feature === 'GENERATE_WITH_AI' && e.featureId === templateId);
    }

    onTemplateGenerationRetry(): void {
        const templateId = this.generatingTemplateId;
        if (!templateId) return;

        this.userCreditsService.removeExhaustion('GENERATE_WITH_AI', templateId).subscribe({
            next: () => {
                this.creditsState.refreshCredits().subscribe();
                this.templateLibraryService.retryTemplateGeneration(templateId).subscribe({
                    next: (res) => {
                        if (res.success) {
                            this.toast.success('Template generation restarted');
                            this.loadTemplates();
                        }
                    },
                    error: () => this.toast.error('Failed to restart template generation')
                });
            },
            error: () => this.toast.error('Failed to remove exhaustion record')
        });
    }

    fileSelectionFetch: FetchFunction = (event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> => {
        if (event.tabId === QueryOptions.KNOWLEDGE_BASE) {
            if (event.dropdownId === 'all') {
                return this.fetchAllKnowledgeBaseFilesForSelection(event);
            }
            if (event.dropdownId) {
                return this.fetchKnowledgeBaseFilesForSelection(event.dropdownId, event);
            }
            this.fileSelectionDropdownOptions = [];
            this.prepareFileSelectionDropdownOptions();
            return of({ items: [], totalRecords: 0 });
        }
        if (event.tabId === QueryOptions.PROJECT_KNOWLEDGE) {
            if (event.dropdownId === 'all') {
                return this.fetchAllProjectKnowledgeFiles(event);
            }
            if (event.dropdownId) {
                return this.fetchProjectKnowledgeFiles(event.dropdownId, event);
            }
            this.fileSelectionDropdownOptions = [];
            this.prepareProjectKnowledgeDropdownOptions();
            return of({ items: [], totalRecords: 0 });
        }
        return of({ items: [], totalRecords: 0 });
    };

    fetchAllFilesForSelection(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        return this.repositoryService.getFiles().pipe(
            map((res) => {
                if (res && res.success) {
                    const files = res.data || [];
                    const filteredFiles = files.filter((file: IFile) => {
                        if (file.type !== 'FILE') return false;
                        if (event.searchQuery && !file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())) {
                            return false;
                        }
                        return true;
                    });

                    const fileItems: FileItem[] = filteredFiles.map((file: IFile) => ({
                        id: file._id,
                        name: file.name || 'Unknown',
                        size: file.size,
                        metadata: {
                            originalFile: file
                        }
                    }));

                    const startIndex = event.page * event.pageSize;
                    const endIndex = startIndex + event.pageSize;
                    const paginatedFiles = fileItems.slice(startIndex, endIndex);

                    return { items: paginatedFiles, totalRecords: fileItems.length };
                }
                return { items: [], totalRecords: 0 };
            })
        );
    }

    fetchAllKnowledgeBaseFilesForSelection(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareFileSelectionDropdownOptions();

        return this.repositoryService.getFiles().pipe(
            map((res) => {
                const allFiles: FileItem[] = [];
                const files = res.data || [];
                const filteredFiles = files.filter((file: any) => {
                    if (file.type !== 'FILE') return false;
                    if (event.searchQuery && !file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())) {
                        return false;
                    }
                    return true;
                });

                filteredFiles.forEach((file: any) => {
                    const firstKb = file.knowledgeBases?.[0];
                    const kbDetails = firstKb ? this.knowledgeBases.find(k => k._id === firstKb._id) : undefined;
                    allFiles.push({
                        id: file._id,
                        name: file.name || 'Unknown',
                        size: file.size,
                        metadata: {
                            kbId: kbDetails?._id ?? "file-repository",
                            kbName: kbDetails?.name ?? "Files",
                            originalFile: file
                        }
                    });
                });

                const startIndex = event.page * event.pageSize;
                const endIndex = startIndex + event.pageSize;
                const paginatedFiles = allFiles.slice(startIndex, endIndex);

                return { items: paginatedFiles, totalRecords: allFiles.length };
            })
        );
    }

    fetchKnowledgeBaseFilesForSelection(kbId: string, event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareFileSelectionDropdownOptions();

        const kb = this.knowledgeBases.find(k => k._id === kbId);
        if (!kb) {
            return of({ items: [], totalRecords: 0 });
        }

        return this.knowledgeBaseService.getKnowledgeBaseStoreById(kbId).pipe(
            map((res) => {
                if (res && res.success && res.data) {
                    const kbFiles = res.data.files?.map((fileItem: any) => fileItem.file).filter((file: IFile) => file) || [];
                    const filteredFiles = kbFiles.filter((file: IFile) => {
                        if (event.searchQuery && !file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())) {
                            return false;
                        }
                        return true;
                    });

                    const fileItems: FileItem[] = filteredFiles.map((file: IFile) => ({
                        id: file._id,
                        name: file.name || 'Unknown',
                        size: file.size,
                        metadata: {
                            kbId: kb._id,
                            kbName: kb.name,
                            originalFile: file
                        }
                    }));

                    const startIndex = event.page * event.pageSize;
                    const endIndex = startIndex + event.pageSize;
                    const paginatedFiles = fileItems.slice(startIndex, endIndex);

                    return { items: paginatedFiles, totalRecords: fileItems.length };
                }
                return { items: [], totalRecords: 0 };
            })
        );
    }

    fetchAllProjectKnowledgeFiles(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareProjectKnowledgeDropdownOptions();
        if (event.parentFolderId) {
            return this.repositoryService.getFolderDetails(event.parentFolderId).pipe(
                map((res: any) => {
                    const childs = res?.data?.childs ?? res?.childs ?? [];
                    const filtered = childs.filter((file: any) =>
                        !event.searchQuery || file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())
                    );
                    const allItems: FileItem[] = filtered.map((file: any) => {
                        if (file.type === 'FOLDER') {
                            return { id: file._id, name: file.name || 'Unknown', isFolder: true, metadata: {} };
                        }
                        const projectId = file.project?._id ?? file.project ?? 'no-project';
                        const projectName = file.project?.name ?? 'No project';
                        const project = this.projects.find((p) => p._id === projectId);
                        return {
                            id: file._id,
                            name: file.name || 'Unknown',
                            size: file.size,
                            metadata: {
                                projectId: project?._id ?? projectId,
                                projectName: project?.name ?? projectName,
                                originalFile: file,
                                isProjectKnowledge: true,
                            },
                        };
                    });
                    const start = event.page * event.pageSize;
                    const end = start + event.pageSize;
                    return { items: allItems.slice(start, end), totalRecords: allItems.length };
                }),
                catchError(() => of({ items: [], totalRecords: 0 }))
            );
        }
        return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT }).pipe(
            map((res) => {
                const raw = (res as any)?.data ?? [];
                const filtered = raw.filter((file: any) =>
                    !event.searchQuery || file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())
                );
                const allFiles: FileItem[] = [];
                filtered.forEach((file: any) => {
                    if (file.type === 'FOLDER') {
                        allFiles.push({ id: file._id, name: file.name || 'Unknown', isFolder: true, metadata: {} });
                    } else {
                        const projectId = file.project?._id ?? file.project ?? 'no-project';
                        const projectName = file.project?.name ?? 'No project';
                        const project = this.projects.find((p) => p._id === projectId);
                        allFiles.push({
                            id: file._id,
                            name: file.name || 'Unknown',
                            size: file.size,
                            metadata: {
                                projectId: project?._id ?? projectId,
                                projectName: project?.name ?? projectName,
                                originalFile: file,
                                isProjectKnowledge: true,
                            },
                        });
                    }
                });
                const start = event.page * event.pageSize;
                const end = start + event.pageSize;
                return { items: allFiles.slice(start, end), totalRecords: allFiles.length };
            })
        );
    }

    fetchProjectKnowledgeFiles(projectId: string, event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareProjectKnowledgeDropdownOptions();
        const project = this.projects.find((p) => p._id === projectId);
        if (!project) return of({ items: [], totalRecords: 0 });
        const allKnowledgeFolderId = ALL_KNOWLEDGE_FOLDER_PREFIX + projectId;
        if (event.parentFolderId === allKnowledgeFolderId) {
            return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT, project: projectId }).pipe(
                map((res: any) => {
                    const raw = res?.data ?? [];
                    const filtered = raw.filter((file: any) =>
                        !event.searchQuery || file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())
                    );
                    const allFiles: FileItem[] = filtered
                        .filter((f: any) => f.type === 'FILE')
                        .map((file: any) => ({
                            id: file._id,
                            name: file.name || 'Unknown',
                            size: file.size,
                            metadata: {
                                projectId: project._id,
                                projectName: project.name,
                                originalFile: file,
                                isProjectKnowledge: true,
                            },
                        }));
                    const start = event.page * event.pageSize;
                    const end = start + event.pageSize;
                    return { items: allFiles.slice(start, end), totalRecords: allFiles.length };
                }),
                catchError(() => of({ items: [], totalRecords: 0 }))
            );
        }
        if (event.parentFolderId) {
            return this.knowledgeBaseService.getKnowledgeBaseStoreById(event.parentFolderId).pipe(
                map((res: any) => {
                    const store = res?.data ?? res;
                    if (!store?.files) return { items: [], totalRecords: 0 };
                    const projectName = project.name;
                    const kbName = store.name || 'Unknown';
                    const breadcrumbLabel = `${projectName} > ${kbName}`;
                    let list = store.files.map((item: any) => ({
                        id: item.file?._id,
                        name: item.file?.name || 'Unknown',
                        size: item.file?.size,
                        metadata: {
                            projectId: project._id,
                            projectName,
                            kbId: store._id,
                            kbName,
                            breadcrumbLabel,
                            originalFile: item.file,
                            isProjectKnowledge: true,
                        },
                    }));
                    if (event.searchQuery) {
                        const q = event.searchQuery.toLowerCase();
                        list = list.filter((f: FileItem) => f.name?.toLowerCase().includes(q));
                    }
                    const start = event.page * event.pageSize;
                    const end = start + event.pageSize;
                    return { items: list.slice(start, end), totalRecords: list.length };
                }),
                catchError(() => of({ items: [], totalRecords: 0 }))
            );
        }
        return this.knowledgeBaseService.getKnowledgeBaseStore({
            scope: KNOWLEDGE_SCOPE.PROJECT,
            project: projectId,
            skip: event.page === 0 ? 0 : event.page * event.pageSize - 1,
            limit: event.page === 0 ? event.pageSize - 1 : event.pageSize,
            search: event.searchQuery || '',
        }).pipe(
            map((res) => {
                const stores = res?.data?.stores ?? [];
                const totalStores = res?.data?.total ?? 0;
                const storeItems: FileItem[] = stores.map((store: any) => ({
                    id: store._id,
                    name: store.name || 'Unknown',
                    isFolder: true,
                    metadata: { kbId: store._id, kbName: store.name },
                }));
                const allKnowledgeFolder: FileItem = {
                    id: allKnowledgeFolderId,
                    name: 'All Knowledge',
                    isFolder: true,
                    metadata: {},
                };
                const items: FileItem[] = event.page === 0 ? [allKnowledgeFolder, ...storeItems] : storeItems;
                const totalRecords = totalStores + 1;
                return { items, totalRecords };
            }),
            catchError(() => of({ items: [], totalRecords: 0 }))
        );
    }

    onFileSelectionSubmit(event: FileSelectionSubmitEvent) {
        // Clear existing selections
        this.selectedExistingFile = null;
        this.selectedFileIds.clear();
        this.selectedKnowledgeBaseFileIds.clear();
        this.selectedKnowledgeBaseFiles = [];
        this.uploadedLocalFiles = [];

        // Handle uploaded files (from local upload)
        if (event.uploadedFiles && event.uploadedFiles.length > 0) {
            // Only take the first file (single file selection)
            this.uploadedLocalFiles = [event.uploadedFiles[0]];
        }

        // Handle selected files
        if (event.allSelectedFiles && event.allSelectedFiles.length > 0) {
            const selectedFile = event.allSelectedFiles[0]; // Only take first file
            const metadata = selectedFile.metadata;

            if (metadata?.originalFile) {
                if (metadata.kbId || metadata.isProjectKnowledge) {
                    this.selectedKnowledgeBaseFileIds.add(selectedFile.id);
                    const file = metadata.originalFile as IFile;
                    this.selectedKnowledgeBaseFiles = [file];
                } else {
                    this.selectedExistingFile = metadata.originalFile as IFile;
                    this.selectedFileIds.add(selectedFile.id);
                }
            }
        }

        // Close the file selection dialog
        this.openFileSelectionDialog = false;

        // Validate that we have a file selected
        const hasFile = this.uploadedLocalFiles.length > 0 ||
            this.selectedExistingFile !== null ||
            this.selectedKnowledgeBaseFileIds.size > 0;

        if (!hasFile) {
            this.toast.error('Please select a file', 'error');
            return;
        }

        // Validate required form fields before creating template
        if (!this.templateName || !this.templateId || !this.selectedDocumentTypeId) {
            this.toast.error('Please fill in all required fields (Template name, Template ID, and Document type)', 'error');
            return;
        }

        // Directly create the template after file selection
        this.createTemplate();
    }

    onFileSelectionCancel() {
        this.openFileSelectionDialog = false;
    }

    get currentDate(): Date {
        return new Date();
    }

    ngOnDestroy() {
        if (this.templateGenerationStatusSubscription) {
            this.templateGenerationStatusSubscription.unsubscribe();
        }

        // Clear all completion timeouts
        this.completionTimeouts.forEach(timeout => clearTimeout(timeout));
        this.completionTimeouts.clear();
    }
}

